import React, { useMemo, useState } from "react";
import { Popover } from "@headlessui/react";
import capitalizeFirstLetter from "../../utils/capitalizeFirstLetter";

interface Substitution {
  label: string;
  value: string;
}

interface SubstitutionDropdownButtonProps {
  substitutions: Substitution[];
  onSelect?: (value: string) => void;
}

export function SubstitutionDropdownButton({
  substitutions,
  onSelect,
}: SubstitutionDropdownButtonProps) {
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  // Group substitutions by prefix before the first '.' or as root if no dot
  const groupedSubstitutions = useMemo(() => {
    return substitutions.reduce<Record<string, Substitution[]>>((acc, substitution) => {
      if (substitution.value.includes(".")) {
        const [prefix] = substitution.value.split(".");
        if (!acc[prefix]) acc[prefix] = [];
        acc[prefix].push(substitution);
      } else {
        if (!acc["__root"]) acc["__root"] = [];
        acc["__root"].push(substitution);
      }
      return acc;
    }, {});
  }, [substitutions]);

  return (
    // @ts-ignore
    <Popover className="relative inline-block mt-1">
      {/* @ts-ignore */}
      <Popover.Button className="text-center w-full md:w-auto items-center px-3 py-2 border border-transparent text-xs leading-4 font-medium rounded-md shadow-sm text-white bg-etpink-600 hover:bg-etpink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-500">
        Personalise
      </Popover.Button>
      {/* @ts-ignore */}
      <Popover.Panel className="absolute z-10 mt-2 w-56 bg-white border border-gray-200 rounded shadow-lg">
        <div>
          {/* Render root substitutions as top-level items */}
          {groupedSubstitutions["__root"] &&
            groupedSubstitutions["__root"].map((sub) => (
              <button
                type="button"
                key={sub.value}
                className="block w-full text-left px-4 py-2 hover:bg-etpink-500/10"
                onClick={() => {
                  onSelect?.(sub.value);
                  setActiveGroup(null);
                }}
              >
                {sub.label}
              </button>
            ))}

          {/* Render grouped substitutions with submenu */}
          {Object.entries(groupedSubstitutions)
            .filter(([group]) => group !== "__root")
            .map(([group, subs]) => (
              <div
                key={group}
                className="relative"
                onMouseEnter={() => setActiveGroup(group)}
                onMouseLeave={() => setActiveGroup(null)}
              >
                <div
                  className={`px-4 py-2 cursor-pointer hover:bg-etpink-500/10 flex justify-between items-center ${activeGroup === group ? "bg-etpink-500/20" : ""
                    }`}
                >
                  {capitalizeFirstLetter(group)}
                  <span className="ml-2">&raquo;</span>
                </div>
                {/* Submenu */}
                {activeGroup === group && (
                  <div className="absolute top-0 left-full w-56 bg-white border border-gray-200 rounded shadow-lg z-20">
                    {subs.map((sub) => (
                      <button
                        type="button"
                        key={sub.value}
                        className="block w-full text-left px-4 py-2 hover:bg-etpink-500/10"
                        onClick={() => {
                          onSelect?.(sub.value);
                          setActiveGroup(null);
                        }}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
        </div>
      </Popover.Panel>
    </Popover>
  );
}